<template>
    <div>
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h5 class="page-title">Make Sales</h5>
          <p class="page-sub">Record a new sale transaction for your customer</p>
        </div>
        <button type="button" @click="$router.go(-1)" class="btn-outline">
          <font-awesome-icon icon="arrow-left" /> Back
        </button>
      </div>
  
      <!-- Validation Errors -->
      <div v-if="validationErrors?.length" class="error-banner">
        <font-awesome-icon icon="exclamation-circle" class="error-icon" />
        <ul class="mb-0 ps-1">
          <li v-for="(err, i) in validationErrors" :key="i">{{ err }}</li>
        </ul>
      </div>
  
      <!-- Loading overlay -->
      <div v-if="loading" class="loading-overlay">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
      </div>
  
      <form @submit.prevent="makeSale">
  
        <!-- Sale Details -->
        <div class="form-card mb-4">
          <div class="section-header">
            <div class="section-icon section-icon--blue">
              <font-awesome-icon icon="receipt" />
            </div>
            <div>
              <p class="section-title">Sale Details</p>
              <p class="section-sub">Set date, store and customer</p>
            </div>
          </div>
  
          <div class="fields-grid">
            <div class="field-group">
              <label class="field-label">Date & Time <span class="req">*</span></label>
              <div class="datepicker-wrap">
                <font-awesome-icon icon="calendar" class="datepicker-icon" />
                <Datepicker v-model="date" :enableTimePicker="true" class="clean-datepicker" />
              </div>
            </div>
            <div class="field-group">
              <label class="field-label">Store <span class="req">*</span></label>
              <Multiselect
                v-model="store_id" label="name" :options="getStore"
                placeholder="Select store" :searchable="true"
                :create-option="false" :close-on-select="true"
                @select="getProductInStore(store_id)"
                class="custom-multiselect"
              />
            </div>
            <div class="field-group">
              <label class="field-label">Customer <span class="req">*</span></label>
              <Multiselect
                v-model="customer" label="name" :options="customer_arr"
                placeholder="Select or create customer" :searchable="true"
                :create-option="true" :close-on-select="true"
                class="custom-multiselect"
              />
              <span class="field-hint">Search or type to create a new customer</span>
            </div>
          </div>
        </div>
  
        <!-- Products -->
        <div class="form-card mb-4">
          <div class="section-header">
            <div class="section-icon section-icon--teal">
              <font-awesome-icon icon="shopping-cart" />
            </div>
            <div>
              <p class="section-title">Products</p>
              <p class="section-sub">Select products and quantities to sell</p>
            </div>
          </div>
  
          <div class="table-scroll-wrap">
            <table class="adj-table">
              <thead>
                <tr>
                  <th style="min-width:200px">Product</th>
                  <th style="min-width:90px">Avail. Qty</th>
                  <th style="min-width:150px">Variant / UOM</th>
                  <th style="min-width:80px">Qty</th>
                  <th style="min-width:110px">Price</th>
                  <th style="min-width:110px">Discount</th>
                  <th style="min-width:100px">Tax</th>
                  <th style="min-width:110px">Amount</th>
                  <th style="min-width:44px"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(p, index) in product" :key="index">
  
                  <td>
                    <Multiselect
                      v-model="p.product_id" label="name" :options="getProducts"
                      :canClear="false" :searchable="true"
                      :create-option="false" :close-on-select="true"
                      @select="handleChange(p.product_id, index)"
                      placeholder="Select product"
                      class="custom-multiselect custom-multiselect--sm"
                    />
                  </td>
  
                  <td>
                    <input disabled type="text" class="tbl-input tbl-input--center" v-model="p.available_qty" />
                  </td>
  
                  <td>
                    <select v-if="p.variant.length > 0" class="tbl-input tbl-select"
                      v-model="p.product_variant_id"
                      @change="handleVariant(p, index, p.product_variant_id)">
                      <option disabled value="">Select variant</option>
                      <option v-for="variant in p.variant" :key="variant.id" :value="variant.id">
                        {{ variant.variant_name }}
                      </option>
                    </select>
                    <input v-else type="text" class="tbl-input" :value="p.unit_of_measurement" disabled />
                  </td>
  
                  <td>
                    <input type="number" class="tbl-input tbl-input--center" min="1"
                      v-model="p.quantity" :disabled="p.disable_field"
                      @keyup="calculateAmount(p)"
                      onkeypress="return /^[0-9]+$/i.test(event.key)" />
                  </td>
  
                  <td>
                    <input type="number" class="tbl-input" min="0" step="any"
                      v-model="p.selling_price"
                      :disabled="p.disable_field || permission.create_price_change !== 1"
                      @keyup="calculateAmount(p)" />
                  </td>
  
                  <td>
                    <input type="number" class="tbl-input" min="0" step="any"
                      v-model="p.discount"
                      :disabled="p.disable_field || permission.create_discount !== 1"
                      @keyup="calculateAmount(p)" />
                  </td>
  
                  <td>
                    <input type="number" class="tbl-input" v-model="p.tax_amount" disabled />
                  </td>
  
                  <td>
                    <input type="number" class="tbl-input tbl-input--amount" min="0" step="any"
                      v-model="p.amount" :disabled="p.disable_field"
                      @keyup="calculateTotal(p)" />
                  </td>
  
                  <td>
                    <button class="btn-remove-row" type="button"
                      @click.prevent="removeProduct(index, calculateTotal)"
                      title="Remove row">
                      <font-awesome-icon icon="times" />
                    </button>
                  </td>
  
                </tr>
              </tbody>
            </table>
          </div>
  
          <div class="table-footer-actions">
            <button class="btn-add-row" type="button" @click="addProduct">
              <font-awesome-icon icon="plus" class="me-2" /> Add Product
            </button>
          </div>
  
          <!-- Totals Summary -->
          <div class="totals-summary">
            <div class="totals-item">
              <span class="totals-label">Subtotal</span>
              <span class="totals-value">{{ $filters.formatMoney(form.total_cost, currentUserCurrency?.symbol) }}</span>
            </div>
            <div class="totals-sep">—</div>
            <div class="totals-item">
              <span class="totals-label">Discount</span>
              <span class="totals-value totals-value--discount">{{ $filters.formatMoney(form.total_discount, currentUserCurrency?.symbol) }}</span>
            </div>
            <div class="totals-sep">+</div>
            <div class="totals-item">
              <span class="totals-label">Tax</span>
              <span class="totals-value">{{ $filters.formatMoney(form.total_tax, currentUserCurrency?.symbol) }}</span>
            </div>
            <div class="totals-sep">=</div>
            <div class="totals-item totals-item--grand">
              <span class="totals-label">Grand Total</span>
              <span class="totals-value totals-value--grand">{{ $filters.formatMoney(form.grand_total, currentUserCurrency?.symbol) }}</span>
            </div>
          </div>
        </div>
  
        <!-- Payment -->
        <div class="form-card mb-4">
          <div class="section-header">
            <div class="section-icon section-icon--green">
              <font-awesome-icon icon="credit-card" />
            </div>
            <div>
              <p class="section-title">Payment</p>
              <p class="section-sub">Choose payment method and enter amounts</p>
            </div>
          </div>
  
          <div class="fields-grid">
  
            <div class="field-group">
              <label class="field-label">Payment Type</label>
              <select v-model="form.payment_type" class="field-input field-select" @change="handlePaymentType()">
                <option :value="pay.value" v-for="pay in paymentTypes" :key="pay.id">{{ pay.name }}</option>
              </select>
              <span class="field-error" v-if="split_payment_error">{{ split_payment_error }}</span>
            </div>
  
            <div class="field-group"
              v-show="form.payment_type == 'cash' || form.payment_type == 'card' || form.payment_type == 'part'">
              <label class="field-label">Paid Amount</label>
              <input type="number" min="0" step="any" class="field-input"
                v-model="form.paid_amount" :disabled="paid_disabled"
                @keyup="handlePaidInput" />
              <span class="field-error" v-if="paid_amount_error">{{ paid_amount_error }}</span>
            </div>
  
            <div class="field-group" v-show="form.payment_type == 'cash' || form.payment_type == 'card'">
              <label class="field-label">Change</label>
              <input type="number" min="0" step="any" class="field-input field-input--readonly" disabled v-model="form.change" />
            </div>
  
            <div class="field-group" v-show="form.payment_type == 'credit' || form.payment_type == 'part'">
              <label class="field-label">Balance</label>
              <input type="text" class="field-input field-input--readonly" v-model="form.balance" disabled />
            </div>
  
            <div class="field-group" v-show="form.payment_type == 'split'">
              <label class="field-label">Cash</label>
              <input type="number" class="field-input" step="any" name="cash"
                v-model="form.cash" :disabled="cash_disabled"
                @keyup="handleSplitpayment($event)" />
              <span class="field-error" v-if="split_cash_error">{{ split_cash_error }}</span>
            </div>
  
            <div class="field-group" v-show="form.payment_type == 'split'">
              <label class="field-label">Card</label>
              <input type="number" min="0" step="any" class="field-input" name="card"
                v-model="form.card" :disabled="card_disabled"
                @keyup="handleSplitpayment($event)" />
              <span class="field-error" v-if="split_card_error">{{ split_card_error }}</span>
            </div>
  
          </div>
        </div>
  
        <!-- Footer -->
        <div class="form-footer">
          <button type="button" @click="$router.go(-1)" class="btn-outline">Cancel</button>
          <button type="submit" class="btn-primary-action" :disabled="crudLoading">
            <span v-if="crudLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
            <font-awesome-icon v-else icon="check" class="me-2" />
            {{ crudLoading ? 'Processing...' : 'Complete Sale' }}
          </button>
        </div>
  
      </form>
    </div>
  </template>
  
  <style src="@vueform/multiselect/themes/default.css"></style>
  
  <script>
  import { ref } from '@vue/reactivity';
  import { onMounted } from '@vue/runtime-core';
  import axios from 'axios';
  import { useReusable } from '../../../lib/reusable';
  import Multiselect from '@vueform/multiselect';
  import { useToast } from 'vue-toastification';
  import { useRouter } from 'vue-router';
  import { reusableMixin } from '../../../lib/reusableMixin';
  
  const EMPTY_PRODUCT = () => ({
    tax_amount: '', product_id: '', available_qty: 0,
    selling_price: 0, cost_price: 0,
    discount: (0).toFixed(2), tax_pecentage: (0).toFixed(2),
    variant: [], tax: [], product_variant_id: '',
    quantity: 1, amount: null, sales_value: 0, cost_value: 0,
    disable_field: true,
  });
  
  export default {
    components: { Multiselect },
    mixins: [reusableMixin],
  
    setup() {
      const toast            = useToast();
      const router           = useRouter();
      const validationErrors = ref([]);
      const store_id         = ref(null);
      const customer_arr     = ref([]);
      const customer         = ref(null);
      const date             = ref(new Date());
      const crudLoading      = ref(false);
      const loading          = ref(false);
      const getProducts      = ref([]);
      const taxes            = ref([]);
      const paid_disabled    = ref(true);
      const cash_disabled    = ref(true);
      const card_disabled    = ref(true);
      const paid_amount_error   = ref('');
      const split_payment_error = ref('');
      const split_cash_error    = ref('');
      const split_card_error    = ref('');
  
      const product = ref(Array.from({ length: 5 }, EMPTY_PRODUCT));
  
      const form = ref({
        total_discount: (0).toFixed(2), total_tax: (0).toFixed(2),
        payment_type: 'cash', total_cost: (0).toFixed(2),
        grand_total: (0).toFixed(2), paid_amount: null,
        change: null, balance: null,
        cash: (0).toFixed(2), card: (0).toFixed(2),
      });
  
      const { removeProduct, permission, paymentTypes, allPaymentTypes, getStore, allStore } = useReusable(product);
  
      const clearFields = (p) => {
        Object.assign(p, EMPTY_PRODUCT());
        Object.assign(form.value, {
          total_discount: (0).toFixed(2), total_tax: (0).toFixed(2),
          payment_type: 'cash', total_cost: (0).toFixed(2),
          grand_total: (0).toFixed(2), paid_amount: null,
          change: null, balance: null,
          cash: (0).toFixed(2), card: (0).toFixed(2),
        });
      };
  
      const getProductInStore = async (storeId) => {
        getProducts.value = [];
        const res      = await axios.get(`/admin/store/product-in-store/${storeId}`);
        const products = res?.data?.data?.data;
        getProducts.value = products.map(p => ({
          value: p.id,
          name: p.bar_code ? `${p.product_name} (${p.bar_code})` : p.product_name,
        }));
        product.value.forEach(p => clearFields(p));
      };
  
      const addProduct = () => product.value.push({ ...EMPTY_PRODUCT(), quantity: 1 });
  
      const handleSplitpayment = (event) => {
        const name = event.target.name;
        const cash = form.value.cash || 0;
        const card = form.value.card || 0;
        if (name === 'cash') {
          form.value.card = parseFloat(form.value.grand_total - cash).toFixed(2);
          if (Math.sign(form.value.card) === -1) { form.value.card = (0).toFixed(2); form.value.cash = parseFloat(form.value.grand_total).toFixed(2); }
        } else {
          form.value.cash = parseFloat(form.value.grand_total - card).toFixed(2);
          if (Math.sign(form.value.cash) === -1) { form.value.cash = (0).toFixed(2); form.value.card = parseFloat(form.value.grand_total).toFixed(2); }
        }
      };
  
      const handlePaymentType = () => {
        const t = form.value.payment_type;
        if (t === 'credit') {
          Object.assign(form.value, { balance: form.value.grand_total, cash: null, card: null, change: null, paid_amount: null });
        } else if (t === 'part') {
          Object.assign(form.value, { change: null, cash: null, card: null, balance: form.value.grand_total, paid_amount: (0).toFixed(2) });
        } else if (t === 'split') {
          Object.assign(form.value, { balance: null, change: null, paid_amount: null });
        } else if (t === 'cash' || t === 'card') {
          Object.assign(form.value, { cash: null, card: null, balance: null, paid_amount: form.value.grand_total, change: (0).toFixed(2) });
        }
      };
  
      const calculateTax = (p) => {
        const taxPct = p?.tax?.reduce((sum, t) => {
          const pct = parseFloat(t.tax_percentage ?? t.percentage ?? 0);
          return sum + (isNaN(pct) ? 0 : pct);
        }, 0) ?? 0;
        const amt = parseFloat(p?.amount) || 0;
        const disc = parseFloat(p?.discount) || 0;
        p.tax_amount    = (amt * taxPct / 100).toFixed(2);
        p.tax_pecentage = ((amt - disc) * taxPct / 100);
      };
  
      const calculateAmount = (p) => {
        p.amount = parseFloat(p.selling_price * p.quantity).toFixed(2);
        if (Math.sign(p.amount)    === -1) p.amount    = (0).toFixed(2);
        if (Math.sign(p.quantity)  === -1) p.quantity  = Math.abs(p.quantity);
        if (Math.sign(p.discount)  === -1) p.discount  = (0).toFixed(2);
        if (isNaN(p.amount)) p.amount = '?';
        if (p.quantity > 0 || p.quantity === '') calculateTotal(p);
      };
  
      const calculateTotal = (p) => {
        calculateTax(p);
        const sum = (arr, key) => arr.reduce((a, b) => { const v = parseFloat(b[key]); return a + (isNaN(v) ? 0 : v); }, 0);
        form.value.total_discount = sum(product.value, 'discount').toFixed(2);
        form.value.total_cost     = sum(product.value, 'amount').toFixed(2);
        form.value.total_tax      = sum(product.value, 'tax_amount').toFixed(2);
        form.value.grand_total    = (parseFloat(form.value.total_tax) + parseFloat(form.value.total_cost) - parseFloat(form.value.total_discount)).toFixed(2);
        form.value.paid_amount    = form.value.grand_total;
        const hasAmount = form.value.paid_amount !== '?';
        paid_disabled.value = !hasAmount; cash_disabled.value = !hasAmount; card_disabled.value = !hasAmount;
        if (form.value.payment_type === 'cash' || form.value.payment_type === 'card')
          form.value.change = (form.value.paid_amount - form.value.grand_total).toFixed(2);
        if (form.value.payment_type === 'credit') form.value.balance = form.value.grand_total;
        p.sales_value = parseFloat(p.amount).toFixed(2);
        if (p.product_variant_id) {
          const variant = p.variant.find(({ id }) => id == p.product_variant_id);
          p.cost_value = (p.cost_price * p.quantity * variant?.variant_quantity).toFixed(2);
        } else {
          p.cost_value = (parseFloat(p.cost_price) * p.quantity).toFixed(2);
        }
      };
  
      const handlePaidInput = () => {
        if (form.value.payment_type === 'part') {
          if (form.value.paid_amount > form.value.grand_total) {
            form.value.paid_amount = form.value.grand_total;
            form.value.balance     = '0.00';
            paid_amount_error.value = 'Paid amount must not exceed total';
            setTimeout(() => { paid_amount_error.value = ''; }, 2000);
          } else {
            form.value.balance = (form.value.grand_total - form.value.paid_amount).toFixed(2);
          }
        } else if (form.value.payment_type === 'cash' || form.value.payment_type === 'card') {
          form.value.change = form.value.paid_amount > form.value.grand_total
            ? (form.value.paid_amount - form.value.grand_total).toFixed(2) : null;
        }
      };
  
      const handleChange = async (id, index) => {
        const res            = await axios.get(`/inventory/product/${id}?store_id=${store_id.value}`);
        const single_product = res?.data?.data;
        const variant        = single_product.variants;
        const occurrences    = product.value.filter(v => v.product_id == single_product.id);
        if (occurrences.length > 1) {
          Object.assign(product.value[index], EMPTY_PRODUCT());
          toast.warning('Product already selected');
          calculateTotal(product.value[index]); return;
        }
        if (product.value.filter(v => v.product_id === '').length === 0) addProduct();
        const hasVariants = variant.length > 0;
        if (single_product?.store_product == null) toast.warning('This product is not in the selected store');
        Object.assign(product.value[index], {
          selling_price: parseFloat(hasVariants ? 0 : single_product.selling_price).toFixed(2),
          cost_price:    parseFloat(single_product.cost_price).toFixed(2),
          available_qty: single_product?.store_qty ?? 0,
          best_before:   single_product.best_before,
          amount:        parseFloat(hasVariants ? 0 : single_product.selling_price * product.value[index].quantity).toFixed(2),
          variant,
          tax: single_product?.tax ?? [],
          disable_field: false,
          unit_of_measurement: single_product?.unit_of_measurement,
          product_variant_id:  '',
        });
        calculateTotal(product.value[index]);
      };
  
      const handleVariant = (p, index, vId) => {
        if (p.variant.length > 0) {
          const variant = p.variant.find(({ id }) => id == vId);
          if (!variant) return;
          product.value[index].selling_price = parseFloat(variant.variant_selling_price).toFixed(2);
          calculateAmount(product.value[index]);
        }
      };
  
      const allcustomers = async () => {
        const res = await axios.get('/settings/customers/all');
        customer_arr.value = res?.data?.data?.map(c => ({ value: c.id, name: c.customer_name })) ?? [];
      };
  
      const makeSale = async () => {
        crudLoading.value = true;
        const valid_products = product.value.filter(p => p.product_id !== '');
        if (form.value.payment_type === 'split') {
          const sum = parseFloat(form.value.cash) + parseFloat(form.value.card);
          if (sum !== parseFloat(form.value.grand_total)) {
            crudLoading.value = false;
            toast.error('Cash and card values must equal the grand total'); return;
          }
        }
        axios.post('/inventory/sales', {
          payment_type: form.value.payment_type, total_amount: form.value.grand_total,
          amount_paid: form.value.paid_amount, change: form.value.change,
          balance: form.value.balance, cash: form.value.cash, card: form.value.card,
          store_id: store_id.value, customer_id: customer.value,
          time: date.value.toTimeString().slice(0, 8),
          date: date.value.toISOString().split('T')[0],
          sales_item: valid_products,
        }).then(res => {
          crudLoading.value = false;
          router.push('/sales-receipt/' + res.data.data.id);
          toast.success(res.data.message);
        }).catch(error => {
          crudLoading.value = false;
          if (error.response.status == 422) { toast.warning('There are errors in your input'); validationErrors.value = Array.isArray(error.response.data.message) ? error.response.data.message : (error.response.data.message ? [String(error.response.data.message)] : []); }
          if (error.response.status == 400) { validationErrors.value = Array.isArray(error.response.data.message) ? error.response.data.message : (error.response.data.message ? [String(error.response.data.message)] : []); toast.error('Bad request, please check your input'); }
          if (error.response.status == 500) toast.error('An error occurred, please try again');
        });
      };
  
      const defaultStore = async () => {
        axios.get('/admin/store/default-store').then(res => {
          store_id.value = res?.data?.data?.id;
          getProductInStore(store_id.value);
          loading.value  = false;
        }).catch(() => { loading.value = false; });
      };
  
      const defaultCustomer = async () => {
        axios.get('/settings/customers/default-customer').then(res => {
          customer.value = res?.data?.data.id;
        }).catch(() => {});
      };
  
      onMounted(() => {
        allcustomers(); allStore(); allPaymentTypes();
        setTimeout(() => { defaultStore(); defaultCustomer(); }, 500);
      });
  
      return {
        loading, store_id, getProductInStore, removeProduct, permission,
        getStore, handleChange, product, getProducts, customer_arr, customer,
        allcustomers, addProduct, calculateTotal, form, date, calculateAmount,
        paid_disabled, cash_disabled, card_disabled,
        paid_amount_error, split_payment_error, split_cash_error, split_card_error,
        validationErrors, handlePaidInput, handlePaymentType, handleSplitpayment,
        makeSale, paymentTypes, allPaymentTypes, taxes, handleVariant, crudLoading,
      };
    },
  };
  </script>
  
  <style scoped>
  /* ── Page Header ── */
  .page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
  .page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub    { font-size:13px; color:#7a90b0; margin:0; }
  
  /* ── Error Banner ── */
  .error-banner { display:flex; align-items:flex-start; gap:10px; background:#fff5f5; border:1px solid #fac5c5; border-radius:10px; padding:12px 16px; margin-bottom:20px; color:#e03535; font-size:13px; }
  .error-icon   { font-size:16px; margin-top:2px; flex-shrink:0; }
  
  /* ── Loading Overlay ── */
  .loading-overlay { position:fixed; inset:0; background:rgba(255,255,255,.7); display:flex; align-items:center; justify-content:center; z-index:9999; }
  
  /* ── Form Card ── */
  .form-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
  .mb-4      { margin-bottom:16px; }
  
  /* ── Section Header ── */
  .section-header { display:flex; align-items:center; gap:14px; margin-bottom:18px; }
  .section-icon   { width:42px; height:42px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
  .section-icon--blue  { background:rgba(0,74,173,.1);   color:#004AAD; }
  .section-icon--teal  { background:rgba(13,148,136,.1); color:#0d9488; }
  .section-icon--green { background:rgba(22,163,74,.1);  color:#16a34a; }
  .section-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .section-sub   { font-size:12px; color:#7a90b0; margin:0; }
  
  /* ── Fields ── */
  .fields-grid   { display:grid; grid-template-columns:repeat(3,1fr); gap:16px; }
  .field-group   { display:flex; flex-direction:column; gap:6px; }
  .field-label   { font-size:13px; font-weight:600; color:#1a2b45; }
  .req           { color:#e03535; }
  .field-hint    { font-size:11.5px; color:#7a90b0; }
  .field-error   { font-size:11.5px; color:#e03535; }
  
  .field-input {
    padding:9px 12px; border:1px solid #dde5f0; border-radius:8px;
    font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%;
    transition:border-color .15s, box-shadow .15s; font-family:inherit;
  }
  .field-input:focus    { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  .field-input:disabled { background:#f8fafc; color:#aab8cc; cursor:not-allowed; }
  .field-input--readonly { background:#f8fafc; }
  .field-select { appearance:auto; cursor:pointer; }
  
  /* ── Datepicker ── */
  .datepicker-wrap { position:relative; }
  .datepicker-icon { position:absolute; left:11px; top:50%; transform:translateY(-50%); color:#7a90b0; font-size:13px; z-index:2; pointer-events:none; }
  :deep(.clean-datepicker) { border:1px solid #dde5f0 !important; border-radius:8px !important; background:#fff !important; padding-left:32px !important; }
  :deep(.clean-datepicker input) { font-size:13.5px; color:#1a2b45; }
  :deep(.clean-datepicker.is-active) { border-color:#004AAD !important; box-shadow:0 0 0 3px rgba(0,74,173,.08) !important; }
  
  /* ── Multiselect ── */
  :deep(.custom-multiselect) { --ms-border-color:#dde5f0; --ms-border-radius:8px; --ms-font-size:13.5px; --ms-ring-color:rgba(0,74,173,.08); }
  :deep(.custom-multiselect.is-active) { --ms-border-color:#004AAD; }
  :deep(.custom-multiselect--sm)       { --ms-py:6px; --ms-font-size:13px; }
  
  /* ── Products Table ── */
  .table-scroll-wrap { overflow-x:auto; }
  .adj-table   { width:100%; border-collapse:collapse; }
  .adj-table thead tr { background:#f8fafc; }
  .adj-table thead th { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:10px 8px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
  .adj-table tbody tr { border-bottom:1px solid #f0f4f8; }
  .adj-table tbody tr:last-child { border-bottom:none; }
  .adj-table tbody tr:hover { background:#fafbfc; }
  .adj-table tbody td { padding:8px; vertical-align:top; }
  
  .tbl-input {
    width:100%; padding:7px 9px; border:1px solid #dde5f0; border-radius:7px;
    font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s;
  }
  .tbl-input:focus    { border-color:#004AAD; box-shadow:0 0 0 2px rgba(0,74,173,.08); }
  .tbl-input:disabled { background:#f8fafc; color:#aab8cc; }
  .tbl-select  { appearance:auto; cursor:pointer; }
  .tbl-input--center { text-align:center; }
  .tbl-input--amount { font-weight:700; color:#004AAD; }
  
  .btn-remove-row { width:30px; height:30px; border-radius:7px; background:#fff0f0; border:1px solid #fac5c5; color:#e03535; display:flex; align-items:center; justify-content:center; font-size:12px; cursor:pointer; transition:background .15s; }
  .btn-remove-row:hover { background:#ffe0e0; }
  
  .table-footer-actions { margin-top:12px; margin-bottom:16px; }
  .btn-add-row { display:inline-flex; align-items:center; background:#edf4ff; color:#004AAD; border:1px solid #c5d6f5; font-size:13px; font-weight:600; padding:8px 18px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .btn-add-row:hover { background:#d6e4fa; }
  
  /* ── Totals Summary ── */
  .totals-summary { display:flex; align-items:center; gap:12px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:14px 20px; flex-wrap:wrap; }
  .totals-item    { display:flex; flex-direction:column; gap:2px; }
  .totals-label   { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
  .totals-value   { font-size:14px; font-weight:600; color:#1a2b45; }
  .totals-value--discount { color:#e03535; }
  .totals-sep     { font-size:18px; color:#aab8cc; font-weight:300; margin-top:10px; }
  .totals-item--grand .totals-label { color:#004AAD; }
  .totals-value--grand { font-size:22px; font-weight:800; color:#004AAD; }
  
  /* ── Footer ── */
  .form-footer { display:flex; align-items:center; justify-content:flex-end; gap:10px; margin-top:4px; padding-top:4px; }
  .btn-primary-action { display:inline-flex; align-items:center; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:10px 28px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 20px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .btn-outline:hover { background:#f4f7fb; }
  
  /* ── Remove number spinners ── */
  input[type=number]::-webkit-outer-spin-button,
  input[type=number]::-webkit-inner-spin-button { -webkit-appearance:none; margin:0; }
  input[type=number] { -moz-appearance:textfield; }
  
  /* ── Responsive ── */
  @media(max-width:991px) { .fields-grid { grid-template-columns:repeat(2,1fr); } }
  @media(max-width:576px) {
    .fields-grid { grid-template-columns:1fr; }
    .form-card   { padding:16px; }
    .form-footer { flex-direction:column; }
    .btn-primary-action,.btn-outline { width:100%; justify-content:center; }
    .totals-summary { flex-direction:column; align-items:flex-start; }
    .totals-sep { display:none; }
  }
  </style>